/**
 * Apply Now button component
 * Sticky on mobile, regular on desktop
 * Triggers invisible reCAPTCHA before opening the application modal
 * Shows "already applied" state if the user previously submitted for this job
 */
import React, { useRef } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { useApplicationFormStore } from '@/stores/application-form-store'
import { useTranslation } from '@/hooks/useTranslation'
import { hasAppliedToJob, getAppliedDate } from '@/lib/applied-jobs'

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || '6LeP1JMsAAAAAM2OTfKgNkbyUjqFEZYh96sKgOwp'

export interface ApplyButtonProps {
  jobPostingId: string
  sticky?: boolean
}

export const ApplyButton: React.FC<ApplyButtonProps> = ({
  jobPostingId,
  sticky = false,
}) => {
  const openModal = useApplicationFormStore((state) => state.openModal)
  const setRecaptchaToken = useApplicationFormStore((state) => state.setRecaptchaToken)
  const { t } = useTranslation()
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const jobIdRef = useRef(jobPostingId)
  jobIdRef.current = jobPostingId

  const alreadyApplied = hasAppliedToJob(jobPostingId)

  const handleClick = (): void => {
    recaptchaRef.current?.reset()
    recaptchaRef.current?.execute()
  }

  const handleRecaptchaChange = (token: string | null) => {
    if (token) {
      setRecaptchaToken(token)
      openModal(jobIdRef.current)
    }
  }

  const formatAppliedDate = () => {
    const date = getAppliedDate(jobPostingId)
    if (!date) return ''
    try {
      return new Date(date).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    } catch {
      return ''
    }
  }

  if (alreadyApplied) {
    const appliedDate = formatAppliedDate()
    const appliedContent = (
      <div className="flex items-center gap-2 justify-center">
        <svg className="w-5 h-5 text-emerald-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span className="font-semibold text-emerald-700">
          {t('job.apply.alreadyApplied') || 'Candidatura enviada'}
        </span>
        {appliedDate && (
          <span className="text-sm text-card-text-secondary">
            ({appliedDate})
          </span>
        )}
      </div>
    )

    if (sticky) {
      return (
        <div className="fixed bottom-0 left-0 right-0 bg-surface border-t border-surface-border p-4 z-50">
          <div className="bg-emerald-50 border border-emerald-200 rounded-md py-3 px-6 min-h-[44px]">
            {appliedContent}
          </div>
        </div>
      )
    }

    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-md py-3 px-8 min-h-[44px]">
        {appliedContent}
      </div>
    )
  }

  const buttonClass = sticky
    ? "w-full bg-primary text-surface font-semibold py-3 px-6 rounded-md hover:bg-primary-hover transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-surface min-h-[44px]"
    : "bg-primary text-surface font-semibold py-3 px-8 rounded-md hover:bg-primary-hover transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-surface min-h-[44px] min-w-[44px] shadow-glow"

  const button = (
    <button
      data-apply-button
      onClick={handleClick}
      className={buttonClass}
      aria-label={t('job.apply.ariaLabel')}
    >
      {t('job.apply.button')}
    </button>
  )

  const recaptcha = (
    <ReCAPTCHA
      ref={recaptchaRef}
      sitekey={RECAPTCHA_SITE_KEY}
      size="invisible"
      badge="bottomright"
      onChange={handleRecaptchaChange}
    />
  )

  if (sticky) {
    return (
      <div className="fixed bottom-0 left-0 right-0 bg-surface border-t border-surface-border p-4 z-50">
        {button}
        {recaptcha}
      </div>
    )
  }

  return (
    <>
      {button}
      {recaptcha}
    </>
  )
}
